import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import {
  fetchCompanyFreeTextFields,
  type CompanyFreeTextField,
} from '@/api/v4/organization.api';
import { fetchSpotlightCompanyFreeTextFields } from '@/api/v4/spotlight.api';
import { QueryKey } from '@/config/query-client';
import { getTimeInMs } from '@/helpers/get-time-in-ms';

import { companyIdState } from '../company.state';
import {
  type CompanyFetchMode,
  useCompanyFetchMode,
} from '../company-fetch-context';

const companyFreeTextFieldsFetchers: Record<
  CompanyFetchMode,
  (organizationId: string) => ReturnType<typeof fetchCompanyFreeTextFields>
> = {
  general: fetchCompanyFreeTextFields,
  spotlight: fetchSpotlightCompanyFreeTextFields,
};

export const useCompanyFreeTextFields = () => {
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();

  return useQuery({
    queryKey: [QueryKey.CompanyFreeTextFields, fetchMode, companyId],
    queryFn: async (): Promise<CompanyFreeTextField[]> => {
      if (!companyId) return [];

      return companyFreeTextFieldsFetchers[fetchMode](companyId);
    },
    enabled: !!companyId,
    staleTime: getTimeInMs(5, 'minute'),
  });
};
